import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { AuthShareQuery } from 'dashboard-services/api/auth';
import { CommonEnums } from 'dashboard-services/api/common';
import { DataStorageQuery } from 'dashboard-services/api/dataStorage';
import { DataTransformQuery } from 'dashboard-services/api/dataTransform';
import { S3Query } from 'dashboard-services/api/external';
import { InsightQuery } from 'dashboard-services/api/insight';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums } from 'dashboard-services/api/statuses';
import { CommonService, ReduxService } from 'dashboard-services/services';
import { globalActions, statusBoxActions } from 'primary-components';

import { basicCustomInsightActions } from 'actions/modules/insight';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { pipelinesQueries } from 'queries/modules/datascience';
import { customInsightSelectors } from 'selectors/modules/insight/custom';

import { dataStorageQueries } from '../datastorage';

import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

const { useFromInsightTile } = customInsightSelectors

export default (() => {

  const useSearchInsights = ({ query, insightType, shouldBeLimited, getQueryBuilder, size = CommonService.getRequestSize(), enabled = true, onSelect, shouldRefetch = true, ...rest }) => 
    useInfiniteQuery({
      ...InsightQuery.QUERIES.search(
        getQueryBuilder,
        {
          query,
          size,
          insightType
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam,
      enabled,
      select: onSelect, 
      ...rest
    })

  const useSearchInsightsMutation = ({ getQueryBuilder, query, from = 0, size = CommonService.getRequestSize(), insightType, ...rest }) =>
    useMutation({
      ...InsightQuery.QUERIES.searchMutation(
        getQueryBuilder,
        {
          query,
          from,
          size,
          insightType
        }
      ),
      ...rest
    })

  const useGetInsight = ({ insightName, groupName, version = "latest", isPublic = false, getQueryBuilder, enableRefetch = true, id = undefined, ...rest }) =>
    useQuery({
      ...InsightQuery.QUERIES.getByName(
        getQueryBuilder,
        { 
          insightName,
          groupName,
          version,
          isPublic,
          id
        }
      ),
      refetchOnMount: enableRefetch,
      refetchOnReconnect: enableRefetch,
      refetchOnWindowFocus: false,
      ...rest
    })

  const useGetInsightMutation = ({ insightName, groupName, version = "latest", isPublic = false, getQueryBuilder }) =>
    useMutation({
      ...InsightQuery.QUERIES.getByNameMutation(
        getQueryBuilder,
        { 
          insightName,
          groupName,
          isPublic,
          version
        }
      ),
    })

  const useFavoriteInsights = ({ favoriteInsights, getQueryBuilder, shouldBeLimited, userName }) => {
    const queries = useMemo(() => favoriteInsights.map(fav => ({
            ...InsightQuery.QUERIES.search(
              getQueryBuilder,
              {
                query: `groupName=${fav.groupName}&name=${fav.fileName}`,
                favorite: true,
                size: 1
              },
            ),
            enabled: !!userName && !!fav.groupName && !!fav.fileName,
            refetchInterval: () => shouldBeLimited() ? undefined : 20_000,
            refetchOnWindowFocus: false
          })), [favoriteInsights, getQueryBuilder, shouldBeLimited, userName]),
          combine = useCallback(results => ({
            data: results.map(result => result.data).map(v => v?.items?.[0]).filter(item => item?.fid !== undefined).map(g => ReduxService.updateObject(g, { favorite: true })),
            isPending: results.some(result => result.isPending),
          }), [])
    return useQueries({
      queries,
      combine
    })
  }

  const useGetInsightTags = ({ query, getQueryBuilder, ...rest }) =>
    useQuery({
      ...InsightQuery.QUERIES.getInsightsTags(
        getQueryBuilder,
        {
          query
        }
      ),
      ...rest
    })

  const getImageSelect = data => data.url
  const useGetInsightLogoUrl = ({ getQueryBuilder, insightName, groupName, version = "latest", isPublic, ...rest }) =>
    useQuery({
      ...InsightQuery.QUERIES.getInsightLogo(
        getQueryBuilder,
        {
          insightName,
          groupName,
          version,
          isPublic
        }
      ),
      select: getImageSelect,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      gcTime: Infinity,
      retry: 0,
      ...rest
    })

  const useGetInsightLogoBlob = ({ getQueryBuilder, insightName, groupName, requestPriority, version = "latest", isPublic, ...rest }) => {
    const getPriorityQueryBuilder = useCallback(() => getQueryBuilder({ priority: requestPriority }), [getQueryBuilder, requestPriority]),
          getQueryBuilderS3 = useCallback(() => 
            getPriorityQueryBuilder()
              .withOnParse(response => Promise.resolve(response))
          , [getPriorityQueryBuilder]),
          queryKey = useMemo(() => [...InsightQuery.KEYS.GET_LOGO(), "BLOB", { insightName, groupName, version, isPublic }], [groupName, insightName, isPublic, version])

    return useQuery({
      queryKey,
      queryFn: async ({ signal }) => {
        const { queryFn: getLogoUrl } = InsightQuery.QUERIES.getInsightLogo(
                getPriorityQueryBuilder,
                {
                  insightName,
                  groupName,
                  version,
                  isPublic
                }
              ),
              { url } = await getLogoUrl({ signal }),
              { queryFn: getImage } = S3Query.QUERIES.getQuery(getQueryBuilderS3, { url }),
              response = await getImage({ signal })

        return response.blob()
      },
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      gcTime: Infinity,
      retry: 0,
      ...rest
    })
  }

  const useDeleteInsight = ({ getQueryBuilder, insightName, groupName, removeGroup, onSuccess, queryClient, ...rest }) =>
    useMutation({
      ...InsightQuery.QUERIES.deleteInsight(
        getQueryBuilder,
        {
          insightName,
          groupName,
          removeGroup
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: InsightQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
      ...rest
    })

  const useShareInsight = ({ getQueryBuilder, insightName, groupName, shares, onSuccess, queryClient, ...rest }) =>
    useMutation({
      ...InsightQuery.QUERIES.shareInsight(
        getQueryBuilder,
        {
          insightName,
          groupName,
          shares
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: InsightQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
      ...rest
    })

  const useUnShareInsight = ({ getQueryBuilder, insightName, groupName, emails, onSuccess, queryClient, ...rest }) =>
    useMutation({
      ...InsightQuery.QUERIES.unshareInsight(
        getQueryBuilder,
        {
          insightName,
          groupName,
          emails
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: InsightQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
      ...rest
    })

  const useUpdateIsInsightPublic = ({ getQueryBuilder, insightName, groupName, isPublic, expirationDate, onSuccess, queryClient, ...rest }) =>
    useMutation({
      ...InsightQuery.QUERIES.updateIsInsightPublic(
        getQueryBuilder,
        {
          insightName,
          groupName,
          expirationDate,
          isPublic
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: InsightQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
      ...rest
    })

  const usePatchInsight = ({ getQueryBuilder, insightName, groupName, fields, onSuccess, queryClient, ...rest }) =>
    useMutation({
      ...InsightQuery.QUERIES.patch(
        getQueryBuilder,
        {
          insightName,
          groupName,
          fields
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: InsightQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
      ...rest
    })

  const useAssignRolesToInsight = ({ getQueryBuilder, insightName, groupName, groups, onSuccess, queryClient, ...rest }) =>
    useMutation({
      ...InsightQuery.QUERIES.assignRolesToInsight(
        getQueryBuilder,
        {
          groupName,
          groups,
          insightName
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: InsightQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
      ...rest
    })

  const useGetListOfShared = ({ getQueryBuilder, insightGroupName, insightName }) => useQuery({
    ...InsightQuery.QUERIES.getListOfShared(
      getQueryBuilder,
      {
        groupName: insightGroupName,
        name: insightName
      }
    ),
    select: response => response.items,
    enabled: !!insightGroupName && !!insightName
  })

  const insightTagsSelect = (response = []) => response.map(item => ({ value: item, label: item }))
  const useInsightTags = ({ getQueryBuilder, query }) =>
    useQuery({
      ...InsightQuery.QUERIES.getInsightsTags(
        getQueryBuilder,
        {
          query: query ? `.{0,}${query}.{0,}` : "*",
          sortOrder: CommonEnums.SORT_ORDER.ASCENDING,
        }
      ),
      select: insightTagsSelect
    })

  const useValidatePublicAccessToken = ({ getQueryBuilder, token, ...rest }) =>
    useMutation({
      ...AuthShareQuery.QUERIES.validatePublicAccessToken(
        getQueryBuilder,
        {
          token
        }
      ),
      ...rest
    })

  const useCreateInsight = ({ getQueryBuilder, queryClient, onSuccess, content, description, isWorkflow, groupName, insightName, logoId, shouldCreateGroup, tags, type, useUserToken }) =>
    useMutation({
      ...InsightQuery.QUERIES.create(
        getQueryBuilder,
        {
          content,
          description,
          groupName,
          insightName,
          isWorkflow,
          logoId,
          shouldCreateGroup,
          tags,
          type,
          useUserToken
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: InsightQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
    })

  const useUpdateInsight = ({ getQueryBuilder, queryClient, onSuccess, content, fields, isWorkflow, newGroupName, newInsightName, oldGroupName, oldInsightName, tags, waitForUpload }) =>
    useMutation({
      ...InsightQuery.QUERIES.update(
        getQueryBuilder,
        {
          content,
          fields,
          isWorkflow,
          newGroupName,
          newInsightName,
          oldGroupName,
          oldInsightName,
          tags,
          waitForUpload
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: InsightQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
    })

  const useSaveInsightCalls = ({ onError }) => {
    const dispatch = useDispatch(),
          queryClient = useQueryClient(),
          getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
          getSimpleQueryBuilder = useCallback(() => new Query.Builder(getApiConfigLocal()), [getApiConfigLocal]),
          getQueryBuilderWithValidation = useCallback(() => 
            new Query.Builder(getApiConfigLocal())
              .withShouldTimeOut(false)
              .withOnError(onError)
          , [getApiConfigLocal, onError]),
          getQueryBuilderWithoutParsing = useCallback(() => 
            new Query.Builder(getApiConfigLocal())
              .withShouldTimeOut(false)
              .withOnError(onError)
              .withOnParse(response => Promise.resolve(response))
          , [getApiConfigLocal, onError]),
          { mutateAsync: assignRolesCall } = useAssignRolesToInsight({ getQueryBuilder: getSimpleQueryBuilder, queryClient }),
          { mutateAsync: getPipelineCall } = pipelinesQueries.useGetPipelineMutation({ getQueryBuilder: getSimpleQueryBuilder }),
          { mutateAsync: createInsightCall } = useCreateInsight({ getQueryBuilder: getQueryBuilderWithValidation, queryClient }),
          { mutateAsync: updateInsightCall } = useUpdateInsight({ getQueryBuilder: getQueryBuilderWithValidation, queryClient }),
          { mutateAsync: uploadFileMetadataCall } = filesDatalakeQueries.useUploadFileMetadata({ getQueryBuilder: getQueryBuilderWithValidation }),
          { mutateAsync: uploadFileContentCall } = filesDatalakeQueries.useUploadFileContent({ getQueryBuilder: getSimpleQueryBuilder }),
          { mutateAsync: getFileContentByIdCall } = filesDatalakeQueries.useGetFileContentById({ getQueryBuilder: getQueryBuilderWithoutParsing }),
          toReturn = useMemo(() => ({
            assignRolesCall, 
            getPipelineCall, 
            createInsightCall, 
            updateInsightCall, 
            uploadFileMetadataCall, 
            uploadFileContentCall, 
            getFileContentByIdCall
          }), [assignRolesCall, createInsightCall, getFileContentByIdCall, getPipelineCall, updateInsightCall, uploadFileContentCall, uploadFileMetadataCall])

    return toReturn;
  }

  const useSubmitDataForInsightPublicAccess = ({ getQueryBuilder, insightName, insightGroup, company, email, fullName, discountCode, ...rest }) =>
    useMutation({
      ...InsightQuery.QUERIES.submitDataForInsightPublicAccess(
        getQueryBuilder,
        {
          company,
          discountCode,
          email,
          fullName,
          groupName: insightGroup,
          insightName,
        }
      ),
      ...rest
    })

  const useGetFileOptions = ({ requestId, fileName, groupName }) => {
    const dispatch = useDispatch(),
          getQueryBuilder = useCallback(() => 
            new Query.Builder(dispatch(getApiConfig()))
              .withRequestIdPrefix(requestId)
              .withFetching(false)
              .withShouldDisplayError(false)
              .withProxy(dispatch(basicCustomInsightActions.getProxy()))
          , [dispatch, requestId]),
          getFileOptionsCall = filesDatalakeQueries.useReturnFileContent({ getQueryBuilder, fileName, groupName }),
          getFileOptions = useCallback(() => 
            getFileOptionsCall()
              .then(response => response.content.text())
              .then(response => {
                const itemsAsJson = CommonService.parseJson(response, null)
                if(itemsAsJson !== null && itemsAsJson?.[0]?.value !== undefined && itemsAsJson?.[0]?.label !== undefined) {
                  return itemsAsJson
                } else {
                  let items = response.split(/\r?\n/)
                  if(items.length === 1) {
                    items = response.split(",")
                  }
                  return items.map(s => ({ label: s, value: s }))
                }
              })
          , [getFileOptionsCall])
    return getFileOptions;
  }

  const useGetCurvesDataMutation = ({ getQueryBuilder, contractSort, csvHeaders, dateFormat, endDate, fillBlanks, keys, lastType, lastTypeAmount, lastTypeOnTime, lastTypeOnTimeTimezone, limitContracts, periodType, range, relative, startDate, timezone, ...rest }) =>
    useMutation({
      ...DataTransformQuery.QUERIES.getCurveData(
        getQueryBuilder,
        {
          contractSort,
          csvHeaders,
          dateFormat,
          endDate,
          fillBlanks,
          keys,
          lastType,
          lastTypeAmount,
          lastTypeOnTime,
          lastTypeOnTimeTimezone,
          limitContracts,
          periodType,
          range,
          relative,
          startDate,
          timezone,
          useV2: true
        }
      ),
      ...rest
    })

  const useInputParamsCalls = ({ onError, requestIdPrefix, provider } = {}) => {
    const dispatch = useDispatch(),
          getQueryBuilder = useCallback(() => 
            new Query.Builder(dispatch(getApiConfig()))
              .withFetching(false)
              .withProxy(dispatch(basicCustomInsightActions.getProxy()))
              .withHeader("Target", provider, true)
          , [dispatch, provider]),
          getQueryBuilderNoParsing = useCallback(() => 
            getQueryBuilder()
              .withOnParse(response => Promise.resolve(response))
          , [getQueryBuilder]),
          getQueryBuilderData = useCallback(() => {
            let builder = getQueryBuilder()
              .withRequestIdPrefix(requestIdPrefix)
              .withShouldDisplayError(false)
              .withShouldTimeOut(false)
              .withFetching(false)
            if(onError) {
              builder = builder.withOnError(({ message }) => {
                onError?.()
                if(String(message).startsWith("Could not find")) {
                  dispatch(globalActions.getMessageStore()).error("One of choosen symbols does not exist. Please select different ones.")
                } else {
                  dispatch(globalActions.getMessageStore()).error(message || "Unknown error")
                }
              })
            }
            return builder;
          }, [dispatch, getQueryBuilder, onError, requestIdPrefix]),
          getQueryBuilderSql = useCallback(() => getQueryBuilder()
            .withRequestIdPrefix(requestIdPrefix)
            .withShouldDisplayError(false)
            .withShouldTimeOut(false)
            .withFetching(false), [getQueryBuilder, requestIdPrefix]),
          { mutateAsync: getFileDetailsCall } = filesDatalakeQueries.useGetFileDefinitionMutation({ getQueryBuilder: getQueryBuilderNoParsing }),
          { mutateAsync: searchFilesCall } = filesDatalakeQueries.useSearchFilesMutation({ getQueryBuilder, withStatus: false }),
          { mutateAsync: getDataMutationCall } = dataStorageQueries.useGetDataMutation({ getQueryBuilder: getQueryBuilderData }),
          // A call can name its own environment (provider -> Target), e.g. chart data of a search tile with a specific provider
          getDataCall = useCallback(({ provider: requestProvider, ...args }) => requestProvider
            ? DataStorageQuery.QUERIES.getDataMutation(() => getQueryBuilderData().withHeader("Target", requestProvider, true)).mutationFn(args)
            : getDataMutationCall(args), [getDataMutationCall, getQueryBuilderData]),
          { mutateAsync: getDataFromSqlCall } = dataStorageQueries.useGetDataFromSqlMutation({ getQueryBuilder: getQueryBuilderSql }),
          { mutateAsync: getCurvesDataCall } = useGetCurvesDataMutation({ getQueryBuilder: getQueryBuilderData }),
          calls = useMemo(() => ({ getDataCall, getDataFromSqlCall, getFileDetailsCall, searchFilesCall, getCurvesDataCall }), [getCurvesDataCall, getDataCall, getDataFromSqlCall, getFileDetailsCall, searchFilesCall])
    return calls;
  }

  const emptyObject = {}
  const useInsightDataPrep = ({ dataprep, tileIndex, requestIdPrefix, withFetching = false, withMetadata = false, resolvePattern = true, withEffect = true }) => {
    const dispatch = useDispatch(),
          parsedValue = useMemo(() => {
            let tempValue = dataprep;
            if(typeof tempValue === "string" && tempValue.startsWith("{") && CommonService.isJson(dataprep)) {
              tempValue = JSON.parse(dataprep)
            }
            return tempValue || emptyObject;
          }, [dataprep]),
          title = useFromInsightTile({ tileIndex , paramName: "title" }),
          getQueryBuilder = useCallback(() => 
            new Query.Builder(dispatch(getApiConfig()))
              .withProxy(dispatch(basicCustomInsightActions.getProxy()))
              .withFetching(withFetching)
              .withRequestIdPrefix(requestIdPrefix)
              .withShouldTimeOut(false)
              .withShouldDisplayError(false)
              .withOnError(({ message }) => {
                if(title) {
                  dispatch(statusBoxActions.addItem({ message, title, variant: StatusesEnums.STATUS.ERROR }))
                }
              })
          , [dispatch, requestIdPrefix, withFetching, title]),
          select = useCallback(response => {
            if(response) {
              return response;
            } else {
              dispatch(statusBoxActions.addItem({ message: "User is not authorized to the data prep or the data prep does not exist.", title, variant: StatusesEnums.STATUS.ERROR }))
              return undefined;
            }
          }, [dispatch, title]),
          { data: dataprepResponse } = dataStorageQueries.useGetDataPrepByName({
            getQueryBuilder,
            name: parsedValue.name,
            groupName: parsedValue.groupName,
            version: "latest",
            withMetadata,
            resolvePattern,
            refetchOnWindowFocus: false,
            select
          })

    useEffect(() => {
      if(withEffect) {
        dispatch(basicCustomInsightActions.setDataPrep(dataprepResponse, tileIndex))
      }
    }, [dataprepResponse, dispatch, tileIndex, withEffect])
    
    return dataprepResponse;
  }

  const useGetVersionsByName = ({ getQueryBuilder, insightName, groupName, shouldRefetch = true, shouldBeLimited, size,...rest }) =>
    useInfiniteQuery({
      ...InsightQuery.QUERIES.getInsightVersionsByName(
        getQueryBuilder,
        {
          insightName,
          groupName,
          size
        }
      ),
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      ...rest
    })

  return {
    useSearchInsights,
    useSearchInsightsMutation,
    useGetInsight,
    useFavoriteInsights,
    useGetInsightTags,
    useGetInsightLogoUrl,
    useGetInsightLogoBlob,
    useDeleteInsight,
    useShareInsight,
    useUnShareInsight,
    useUpdateIsInsightPublic,
    usePatchInsight,
    useAssignRolesToInsight,
    useGetListOfShared,
    useGetInsightMutation,
    useInsightTags,
    useValidatePublicAccessToken,
    useCreateInsight,
    useUpdateInsight,
    useSaveInsightCalls,
    useSubmitDataForInsightPublicAccess,
    useGetFileOptions,
    useInputParamsCalls,
    useInsightDataPrep,
    useGetCurvesDataMutation,
    useGetVersionsByName
  }
})()
